import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import '../App.css';
import { translateError } from '../utils/errorMessages';

interface Organization {
  _id: string;
  name: string;
  slug: string;
  subscription?: {
    isDemo?: boolean;
    expiresAt?: string;
  };
}

interface Role {
  _id: string;
  name: string;
  permissions: Record<string, number>;
}

interface Membership {
  _id?: string;
  organization: Organization;
  role: Role;
  joinedAt: string;
  isActive: boolean;
}

interface OrgSelectionModalProps {
  memberships: Membership[];
  onSelect: (orgId: string) => Promise<void>;
  onCancel?: () => void;
}

const OrgSelectionModal: React.FC<OrgSelectionModalProps> = ({
  memberships,
  onSelect,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (orgId: string, orgSlug: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await onSelect(orgId);

      // Read the new token from localStorage (saved by switchOrganization)
      const stored = localStorage.getItem('userInfo');
      const token = stored ? JSON.parse(stored).token : '';

      // Redirect to the organization's subdomain via /auth/callback with token
      // AuthCallback will save the token on the new origin and redirect appropriately
      const currentHost = window.location.hostname;
      let newUrl: string;

      if (currentHost.includes('.localhost')) {
        // Development: my-org.localhost:3000
        newUrl = `http://${orgSlug}.localhost:${window.location.port}/auth/callback?token=${encodeURIComponent(token)}`;
      } else if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
        // Plain localhost - set devTenantSlug and reload
        localStorage.setItem('devTenantSlug', orgSlug);
        window.location.href = '/admin/calendar';
        return;
      } else if (currentHost.endsWith('.eventik.up.railway.app') || currentHost === 'eventik.up.railway.app') {
        // Railway deployment
        newUrl = `https://${orgSlug}.eventik.up.railway.app/auth/callback?token=${encodeURIComponent(token)}`;
      } else if (currentHost.endsWith('.eventik.cz') || currentHost === 'eventik.cz') {
        // Production
        newUrl = `https://${orgSlug}.eventik.cz/auth/callback?token=${encodeURIComponent(token)}`;
      } else {
        // Generic: navigate to the org's subdomain
        const parts = currentHost.split('.');
        if (parts.length >= 2) {
          parts[0] = orgSlug;
        } else {
          parts.unshift(orgSlug);
        }
        newUrl = `${window.location.protocol}//${parts.join('.')}/auth/callback?token=${encodeURIComponent(token)}`;
      }

      window.location.href = newUrl;
    } catch (err: any) {
      setError(translateError(err, t, 'common.error'));
      setIsLoading(false);
    }
  };

  const activeMemberships = memberships.filter(m => m.isActive);

  return (
    <div className="org-selection-modal">
      <div className="org-selection-content">
        <h2 className="org-selection-title">{t('auth.selectOrganization')}</h2>
        <p className="org-selection-subtitle">
          {t('auth.selectOrganizationDesc')}
        </p>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="org-selection-list">
          {activeMemberships.map((membership) => (
            <button
              key={membership.organization._id}
              className="org-selection-item"
              onClick={() => handleSelect(membership.organization._id, membership.organization.slug)}
              disabled={isLoading}
            >
              <span className="org-selection-item-name">
                {membership.organization.name}
                {membership.organization.subscription?.isDemo && (
                  <span className="org-demo-badge" title={t('common.demoBadge')}>
                    DEMO
                  </span>
                )}
              </span>
              <span className="org-selection-item-role">
                {membership.role.name}
              </span>
            </button>
          ))}
        </div>

        {onCancel && (
          <Button
            variant="secondary"
            fullWidth
            onClick={onCancel}
            disabled={isLoading}
            style={{ marginTop: '1rem' }}
          >
            {t('common.cancel')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrgSelectionModal;
